import { EXPERIENCE_IDS } from '@/components/admin/experiences/contracts';
import type { ProtocolLinkRequest } from '@/components/admin/experiences/experience-blockers';
import { viewSearch } from '@/lib/protocol-search';
import { Link } from '@tanstack/react-router';
import { ExternalLink } from 'lucide-react';

/**
 * « Ouvrir le protocole » (analyste.md §3.6, noyau.md §3.7) - straight to that
 * protocol's section Analyste, `?vue=lecture` (N47), under the line of a protocol
 * whose section is incomplete - a level no objective carries, or a reading that is
 * empty, one line per pass -, in the save box as in the pointer box, and from the
 * shell of the experience as from its page editor (review of !474). The address is
 * the one the view « Lecture pour l'analyste » had before #196: the section kept it.
 *
 * Since issue #158 it ends a second line too, a direction of the protocol that
 * cites an objective gone (§8.6 of the refonte): that one is fixed in the
 * conduite of the interview, the view by default - no `?vue=`, and no step
 * either, the selected step not travelling in the URL (§7.1). The view is the
 * request's, so the two lines cannot swap doors.
 *
 * A real link, so a middle click opens it beside the box. A plain one LEAVES
 * the screen, so the box is closed through its `leave` first, which writes the
 * autosave still owed (`SaveBoxHandle`, `PointerBoxHandle`) - a modified click
 * leaves nothing behind and closes nothing.
 *
 * Except on an experience that does not exist YET (`newTab`): nothing of it
 * lives anywhere but in its page (§4), so leaving would lose all of it - and
 * every protocol written before #154 has no reading, so the first save of a
 * noyau experience is exactly where this line shows (and since #196 every protocol
 * written before the section has no level at all). The link then opens a NEW tab,
 * and says so: to the eye by the icon, to a screen reader by its name.
 */

export interface ProtocolReadingLinkProps extends ProtocolLinkRequest {
  /** The experience exists only in this page: open the protocol beside it. */
  readonly newTab: boolean;
  /** The door of the box it sits in, for a navigation off this screen. */
  readonly onLeave: (() => void) | undefined;
}

export function ProtocolReadingLink({
  protocolId,
  view,
  className,
  newTab,
  onLeave,
}: ProtocolReadingLinkProps) {
  return (
    <Link
      to="/admin/protocoles/$protocolId"
      params={{ protocolId }}
      search={viewSearch(view)}
      data-testid={EXPERIENCE_IDS.openProtocolReading}
      {...(newTab ? { target: '_blank', rel: 'noreferrer' } : {})}
      onClick={(event) => {
        if (newTab) return;
        if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
        onLeave?.();
      }}
      className={newTab ? `${className} inline-flex items-center gap-1` : className}
    >
      Ouvrir le protocole
      {newTab ? (
        <>
          <ExternalLink aria-hidden className="size-3.5 shrink-0" />
          <span className="sr-only"> (nouvel onglet)</span>
        </>
      ) : null}
    </Link>
  );
}
